import { ErrorAlert } from "@components/ErrorAlert";
import { GetPayeeEngagementAsPayerExpandParameterInner } from "@wingspanhq/wingspan-api-sdk";
import { WSActions, WSAlert, WSList } from "@wingspanhq/fe-component-library";
import React, { useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  queriesPayeeEngagements,
  queriesPayerRows,
  queriesWorkItems,
  queriesWorkLogs
} from "@query";
import { Layout } from "../../../../shared/components/Layout";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { CardWorkItems } from "../../components/CardWorkItems";
import { activeWorkItemsFilter } from "../../utils/activeWorkItemsFilter";
import { filterActiveWorkItems } from "../../utils/filterActiveWorkItems";
import { WorkLogEngagementDetails } from "../../components/WorkLogEngagementDetails";
import { useModalConvert } from "../../modals/ModalConvert";
import { useModalExit } from "./ModalExit";

export const RouteEdit: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const history = useHistory();
  const workLogId = props.match.params.workLogId;
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery({ workLogId });
  const workLog = queryWorkLog.data;

  // Resolve the client from the work log's engagement.
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: {
        engagements_id: {
          eq: workLog?.payeeEngagementId ?? undefined
        }
      }
    },
    {
      enabled: !!workLog?.payeeEngagementId
    }
  );

  const payerRow = queryPayerRow.data?.data?.[0];
  const payerId = payerRow?.id;
  const payerPayeeEngagementId = workLog?.payeeEngagementId ?? undefined;

  // Expand definitions on the shared engagement read for the work-item form.
  const queryPayerEngagement =
    queriesPayeeEngagements.getPayeeEngagementAsPayer.useQuery(
      {
        payeeEngagementId: payerPayeeEngagementId!,
        expand: new Set([
          GetPayeeEngagementAsPayerExpandParameterInner.WORK_DEFINITIONS
        ])
      },
      { enabled: !!payerPayeeEngagementId }
    );
  const payerPayeeEngagement = queryPayerEngagement.data;

  const workDefinitions = queryPayerEngagement.data?.workDefinitions ?? [];

  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId) {
      return "Log Work";
    }

    if (!payerRow) {
      return "Log Work";
    }

    const payerName = selectorSearchRowName(payerRow);

    if (payerPayeeEngagement) {
      return `Log Work – ${payerName} • ${payerPayeeEngagement.engagementName}`;
    }

    return `Log Work – ${payerName}`;
  }, [payerRow, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  const queryWorkItems = queriesWorkItems.listWorkItems.useQuery({
    filter: activeWorkItemsFilter(workLogId)
  });

  const modalExit = useModalExit();
  const modalConvert = useModalConvert();

  const [createError, setCreateError] = useState(false);

  const isInvoiceCreated = !!workLog?.convertedResourceId;

  return (
    <Layout
      title="Work log"
      onClose={() => {
        modalExit.open();
      }}
    >
      <WSList gap="2XL">
        <WorkLogEngagementDetails
          shimmer={
            queryPayerRow.isInitialLoading ||
            queryPayerEngagement.isInitialLoading
          }
          payerRow={payerRow}
          payerPayeeEngagement={payerPayeeEngagement}
        />

        <ErrorAlert
          role="alert"
          context="WorkLog"
          error={queryPayerEngagement.error}
        />
        <CardWorkItems
          shimmer={
            queryWorkItems.isInitialLoading ||
            queryPayerEngagement.isInitialLoading
          }
          noEdit={isInvoiceCreated}
          workLogId={workLogId}
          payerId={payerId!}
          payerPayeeEngagementId={payerPayeeEngagementId!}
          workItems={queryWorkItems.data?.data || []}
          workDefinitions={workDefinitions}
          resources={{
            payerPayeeEngagement
          }}
        />

        {createError && (
          <WSAlert
            title="Invoice creation error"
            theme="warning"
            icon="alert"
            onDismiss={() => {
              setCreateError(false);
            }}
          >
            There was an error creating the invoice. Please try again. If the
            issue persists, please contact support.
          </WSAlert>
        )}

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: isInvoiceCreated ? "View invoice" : "Create invoice",
              disabled:
                filterActiveWorkItems(queryWorkItems.data?.data || [])
                  .length === 0,
              onClick: () => {
                if (isInvoiceCreated) {
                  history.push(
                    `/member/invoices/${workLog.convertedResourceId}`
                  );
                } else {
                  modalConvert.open({
                    workLogId,
                    onClose: result => {
                      setCreateError(result?.createError || false);
                    }
                  });
                }
              }
            }
          ]}
        />
      </WSList>
    </Layout>
  );
};
